import styled from '@emotion/styled';
import { IconButton, Tooltip, Typography } from '@material-ui/core';
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import { Download } from 'lucide-react';
import { times } from 'lodash';
import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { useIsVisibleForRef } from '../../hooks/useIsVisible';
import { usePromise } from '../../hooks/usePromise';
import { useUnsafeTranslation } from '../../i18next';
import { Centered } from '../../layout/Centered';
import { pdfjs } from '../../services/pdfjs';
import { Loader } from '../Loader';

const usePdfDocumentFromUrl = (url: string) => {
  return usePromise(async () => (await pdfjs()).getDocument(url).promise, [
    url
  ]);
};

const Container = styled('div')((p) => ({
  width: '100%',
  height: '100%',
  overflow: 'auto',
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  backgroundColor: '#F5F5F5'
}));

const PagesContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: 16,
  gap: 16
});

const ToolbarBar = styled('div')((p) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderBottom: p.theme.custom.border.standard,
  padding: `${p.theme.spacing(0.5)}px ${p.theme.spacing(2)}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: p.theme.spacing(2)
}));

const PageContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  position: 'relative'
});

const PageOverlayContainer = styled('div')({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)'
});

const Page = ({
  doc,
  pageNumber,
  containerWidth,
  scrollRef,
  pageRef
}: {
  doc: PDFDocumentProxy;
  pageNumber: number;
  containerWidth: number;
  scrollRef: React.RefObject<HTMLDivElement>;
  pageRef: React.RefObject<HTMLDivElement>;
}) => {
  const { t } = useUnsafeTranslation();
  const { isVisible } = useIsVisibleForRef(pageRef, {
    root: scrollRef.current || null,
    rootMargin: '200px'
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rendering, setRendering] = useState(false);
  const [rendered, setRendered] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isVisible || rendered) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    let cancelled = false;

    const renderPage = async () => {
      try {
        setRendering(true);
        const page = await doc.getPage(pageNumber);

        if (cancelled) return;

        // Calculate scale to fit width with some padding
        const targetWidth = containerWidth - 64; // Account for padding
        const viewport = page.getViewport({ scale: 1 });
        const scale = targetWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale: scale * 2 }); // 2x for sharper rendering

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        canvas.style.width = `${scaledViewport.width / 2}px`;
        canvas.style.height = `${scaledViewport.height / 2}px`;

        await page.render({
          canvasContext: ctx,
          viewport: scaledViewport
        }).promise;

        if (!cancelled) {
          setRendered(true);
          setRendering(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setRendering(false);
        }
      }
    };

    renderPage();

    return () => {
      cancelled = true;
    };
  }, [doc, pageNumber, containerWidth, isVisible, rendered]);

  return (
    <PageContainer ref={pageRef}>
      <canvas
        ref={canvasRef}
        style={{
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          backgroundColor: '#fff'
        }}
      />
      {rendering && (
        <PageOverlayContainer>
          <Loader />
        </PageOverlayContainer>
      )}
      {error && (
        <PageOverlayContainer>
          <Typography variant="caption" color="error">
            {t('Failed to load page.')}
          </Typography>
        </PageOverlayContainer>
      )}
    </PageContainer>
  );
};

export type PdfViewerProps = {
  url: string;
  height?: number | string;
  width?: number | string;
  showPageCount?: boolean;
  showDownload?: boolean;
  downloadFilename?: string;
};

export const PdfViewer = ({
  url,
  height = 500,
  width = '100%',
  showPageCount = false,
  showDownload = false,
  downloadFilename = 'document.pdf'
}: PdfViewerProps) => {
  const { t } = useUnsafeTranslation();
  const [doc, loading, error] = usePdfDocumentFromUrl(url);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(600);
  const [currentPage, setCurrentPage] = useState(1);

  const pageRefs = useMemo(() => {
    if (!doc) {
      return [];
    }
    return times(doc.numPages).map(() => createRef<HTMLDivElement>());
  }, [doc]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const updateWidth = () => {
      setContainerWidth(container.clientWidth);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Track which page is currently visible
  useEffect(() => {
    if (!doc || pageRefs.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the entry with the highest intersection ratio
        let maxRatio = 0;
        let visiblePage = currentPage;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            const index = pageRefs.findIndex(
              (ref) => ref.current === entry.target
            );
            if (index !== -1) {
              visiblePage = index + 1;
            }
          }
        });

        if (maxRatio > 0) {
          setCurrentPage(visiblePage);
        }
      },
      {
        root: scrollRef.current,
        threshold: [0, 0.25, 0.5, 0.75, 1]
      }
    );

    pageRefs.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, [doc, pageRefs, currentPage]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFilename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showToolbar = showPageCount || showDownload;

  return (
    <Container ref={scrollRef} style={{ height, width }}>
      {loading && (
        <Centered style={{ height: '100%' }}>
          <Loader />
        </Centered>
      )}
      {error && (
        <Centered style={{ height: '100%' }}>
          <Typography variant="body2" color="error">
            {t('Failed to load PDF')}
          </Typography>
        </Centered>
      )}
      {doc && containerWidth > 0 && (
        <>
          {showToolbar && (
            <ToolbarBar>
              {showPageCount && (
                <Typography variant="caption">
                  {t('Page {{ current }} of {{ total }}', {
                    current: currentPage,
                    total: doc.numPages
                  })}
                </Typography>
              )}
              {showDownload && (
                <Tooltip title={t('Download PDF') || 'Download PDF'}>
                  <IconButton size="small" onClick={handleDownload}>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              )}
            </ToolbarBar>
          )}
          <PagesContainer>
            {times(doc.numPages).map((i) => (
              <Page
                key={i}
                doc={doc}
                pageNumber={i + 1}
                containerWidth={containerWidth}
                scrollRef={scrollRef}
                pageRef={pageRefs[i]}
              />
            ))}
          </PagesContainer>
        </>
      )}
    </Container>
  );
};
